// PATH: src/components/ResponsiveTable.tsx — tableau qui devient liste de
// cartes sur écran étroit.
//
// Un tableau de huit colonnes ne se lit pas sur un téléphone, et le faire
// défiler horizontalement au doigt est une impasse : on ne sait plus quelle
// ligne on suit. Sous le point de rupture `md`, chaque ligne devient donc une
// carte (titre, détail, actions) — même donnée, même source, autre forme.
//
// Au-dessus de `md`, c'est le Table antd habituel, avec en-tête collant : une
// liste de 200 élèves perdait ses titres de colonnes dès le premier défilement.
import React from "react";
import { Card, Empty, Grid, List, Space, Table, Typography } from "antd";
import type { TableProps } from "antd";

/** Projection d'une ligne vers sa carte (écran étroit). */
export interface RowCard<T> {
  /** Identité de la ligne — ce qu'on lit en premier. */
  title: (row: T) => React.ReactNode;
  /** Informations secondaires, sur une ou deux lignes. */
  description?: (row: T) => React.ReactNode;
  /** Statut ou montant, aligné à droite du titre. */
  extra?: (row: T) => React.ReactNode;
  /** Actions de la ligne, en pied de carte. Les entrées vides sont ignorées. */
  actions?: (row: T) => React.ReactNode[];
}

/**
 * cardActions — pied de carte, ou rien du tout.
 *
 * Une action conditionnelle (« Archiver » réservé aux actifs) laisse un trou
 * dans le tableau retourné : antd rendrait alors une case vide, et une liste
 * de cartes n'aurait pas toutes ses actions au même endroit. Un tableau vide
 * devient `undefined`, pour qu'aucune barre d'actions ne s'affiche.
 */
const cardActions = <T,>(card: RowCard<T>, row: T): React.ReactNode[] | undefined => {
  const list = (card.actions?.(row) ?? []).filter(Boolean);
  return list.length > 0 ? list : undefined;
};

type Props<T> = TableProps<T> & {
  card: RowCard<T>;
  /** Ouverture de la ligne sur écran étroit (le clic d'une carte entière). */
  onRowClick?: (row: T) => void;
};

const keyOf = <T,>(row: T, index: number, rowKey: Props<T>["rowKey"]): React.Key => {
  if (typeof rowKey === "function") return rowKey(row, index);
  if (typeof rowKey === "string") {
    const value = (row as Record<string, unknown>)[rowKey];
    if (typeof value === "string" || typeof value === "number") return value;
  }
  return index;
};

function ResponsiveTable<T extends object>({ card, onRowClick, ...tableProps }: Props<T>) {
  const screens = Grid.useBreakpoint();
  // `screens.md` est indéfini au tout premier rendu (avant mesure) : on ne bascule
  // en cartes qu'une fois la largeur CONNUE et sous le seuil, sinon l'écran large
  // afficherait brièvement la version mobile.
  const narrow = screens.md === false;

  if (!narrow) {
    return <Table<T> sticky {...tableProps} />;
  }

  const rows = (tableProps.dataSource ?? []) as readonly T[];
  const emptyText = (tableProps.locale?.emptyText as React.ReactNode) ?? (
    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Aucun élément" />
  );

  return (
    <List
      loading={tableProps.loading as boolean}
      dataSource={rows as T[]}
      locale={{ emptyText }}
      pagination={
        // La pagination du tableau est réutilisée telle quelle quand elle existe :
        // une liste mobile non paginée chargerait 200 cartes d'un coup.
        tableProps.pagination === false || !tableProps.pagination
          ? undefined
          : { ...(tableProps.pagination as object), size: "small" }
      }
      renderItem={(row, index) => (
        // Clé portée par l'élément plutôt que par `rowKey` de List : celui-ci ne
        // reçoit pas l'index, et le retrouver imposerait un indexOf par ligne.
        <List.Item key={keyOf(row, index, tableProps.rowKey)} style={{ padding: "8px 0", border: "none" }}>
          <Card
            size="small"
            style={{ width: "100%", cursor: onRowClick ? "pointer" : undefined }}
            onClick={onRowClick ? () => onRowClick(row) : undefined}
            actions={cardActions(card, row)}
          >
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start" }}>
              <Typography.Text strong>{card.title(row)}</Typography.Text>
              {card.extra?.(row)}
            </div>
            {card.description && (
              <Space wrap size={8} style={{ marginTop: 6 }}>
                {card.description(row)}
              </Space>
            )}
          </Card>
        </List.Item>
      )}
    />
  );
}

export default ResponsiveTable;
